export type Poll1Question = {
  id: string;
  text: string;
  pts: number;
  window: string;
  evidence_type?: string;
  evidence_required?: boolean;
  evidence_label?: string;
  evidence_placeholder?: string;
};

export type Poll1Tier = {
  min: number;
  max: number;
  score_5pt?: number;
  tier: string;
};

export type Poll1DateBounds = {
  min?: string;
  max?: string;
  errorMessage: string;
};

export function poll1RawFromAnswers(
  answers: (boolean | null)[],
  questions: Poll1Question[]
): number {
  return answers.reduce(
    (sum, ans, i) => sum + (ans === true ? (questions[i]?.pts ?? 0) : 0),
    0
  );
}

export function poll1Lookup(
  raw: number,
  tiers: Poll1Tier[]
): { tier: string; score_5pt: number } {
  for (const t of tiers) {
    const lo = t.min ?? -Infinity;
    const hi = t.max ?? Infinity;
    if (raw >= lo && raw <= hi) {
      return { tier: t.tier, score_5pt: t.score_5pt ?? 0 };
    }
  }
  const last = tiers[tiers.length - 1];
  return { tier: last?.tier ?? "—", score_5pt: last?.score_5pt ?? 0 };
}

/** Sponsor-facing scale: merge adjacent tiers with the same label (e.g. 1–4 Developing). */
export function poll1ScoringScaleRows(
  tiers: Poll1Tier[]
): { label: string; tier: string }[] {
  const merged: { min: number; max: number; tier: string }[] = [];
  for (const row of tiers) {
    const last = merged[merged.length - 1];
    if (last && last.tier === row.tier) {
      last.min = Math.min(last.min, row.min);
      last.max = Math.max(last.max, row.max);
    } else {
      merged.push({ min: row.min, max: row.max, tier: row.tier });
    }
  }
  return merged.map(({ min, max, tier }) => ({
    tier,
    label: min === max ? `${min} pts` : `${min}–${max} pts`,
  }));
}

const toIsoDate = (year: number, monthIndex: number, day: number): string => {
  const m = String(monthIndex + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
};

export const todayIsoDate = (now: Date = new Date()): string =>
  toIsoDate(now.getFullYear(), now.getMonth(), now.getDate());

const parseYearQuarter = (
  period: string
): { year: number; quarter: number } | null => {
  const match = period.trim().match(/^(\d{4})-Q([1-4])$/i);
  if (!match) return null;
  return { year: Number(match[1]), quarter: Number(match[2]) };
};

const shiftYearQuarter = (
  year: number,
  quarter: number,
  delta: number
): { year: number; quarter: number } => {
  let y = year;
  let q = quarter + delta;
  while (q < 1) {
    q += 4;
    y -= 1;
  }
  while (q > 4) {
    q -= 4;
    y += 1;
  }
  return { year: y, quarter: q };
};

const quarterStartIso = (year: number, quarter: number): string =>
  toIsoDate(year, (quarter - 1) * 3, 1);

const quarterEndIso = (year: number, quarter: number): string => {
  const endMonthIndex = quarter * 3; // exclusive month index after quarter
  const lastDay = new Date(year, endMonthIndex, 0).getDate();
  return toIsoDate(year, endMonthIndex - 1, lastDay);
};

/**
 * Allowed evidence date window for Poll 1 date questions.
 * - q2 (QBR): any past date through today (no future)
 * - q3 (on-site): previous two quarters only (period-1 and period-2)
 * - q4 (EBX): full calendar year of the poll period (planned or attended)
 */
export function poll1EvidenceDateBounds(
  questionId: string,
  period: string,
  today: string = todayIsoDate()
): Poll1DateBounds | null {
  if (questionId === "q2") {
    return {
      max: today,
      errorMessage: "Date cannot be in the future.",
    };
  }

  const parsed = parseYearQuarter(period);
  if (!parsed) return null;

  if (questionId === "q3") {
    const prev = shiftYearQuarter(parsed.year, parsed.quarter, -1);
    const prevPrev = shiftYearQuarter(parsed.year, parsed.quarter, -2);
    return {
      min: quarterStartIso(prevPrev.year, prevPrev.quarter),
      max: quarterEndIso(prev.year, prev.quarter),
      errorMessage: "Select a date within the last 2 quarters.",
    };
  }

  if (questionId === "q4") {
    return {
      min: toIsoDate(parsed.year, 0, 1),
      max: toIsoDate(parsed.year, 11, 31),
      errorMessage: "Select a date within this calendar year.",
    };
  }

  return null;
}

export function isPoll1EvidenceDateInBounds(
  value: string,
  bounds: Poll1DateBounds | null
): boolean {
  const iso = String(value || "").trim();
  if (!iso) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  if (!bounds) return true;
  if (bounds.min && iso < bounds.min) return false;
  if (bounds.max && iso > bounds.max) return false;
  return true;
}

export function evidenceComplete(
  answers: (boolean | null)[],
  evidence: Record<string, string>,
  questions: Poll1Question[],
  period?: string
): boolean {
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    if (!q.evidence_type || q.evidence_type === "none") continue;
    if (!q.evidence_required) continue;
    if (answers[i] !== true) continue;
    const val = evidence[q.id];
    if (!val || !String(val).trim()) return false;
    if (q.evidence_type === "date" && period) {
      const bounds = poll1EvidenceDateBounds(q.id, period);
      if (!isPoll1EvidenceDateInBounds(val, bounds)) return false;
    }
  }
  return true;
}

export function buildSubmitEvidence(
  answers: boolean[],
  evidence: Record<string, string>,
  questions: Poll1Question[]
): Record<string, string> {
  const out: Record<string, string> = {};
  questions.forEach((q, i) => {
    if (answers[i] === true && evidence[q.id]?.trim()) {
      out[q.id] = evidence[q.id].trim();
    }
  });
  return out;
}
